import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Eye, Users, Share2, Download, TrendingUp, Calendar,
    Edit, QrCode, Copy, ExternalLink, RefreshCw, BarChart3,
    Shield, Link2, User, Contact, ChevronRight
} from 'lucide-react';
import { rememberOwnCard } from '@/features/card/lib/device';
import { useUnreadConnections } from '@/hooks/useConnections';
import { useAuthStore } from '@/store/authStore';
import { apiMethods } from '@/services/api';
import AnnouncementWidget from '@/components/AnnouncementWidget';
import LiveNotifications from '@/components/LiveNotifications';
import socketService from '@/services/socket';

const Dashboard = () => {
    const { user } = useAuthStore();
    const [copied, setCopied] = useState(false);

    // Establish Socket.IO connection at dashboard level
    useEffect(() => {
        if (user?._id) {
            console.log('🏠 Dashboard: Connecting Socket.IO for user:', user._id);
            socketService.connect(user._id, 'user');
        }

        // Cleanup on unmount
        return () => {
            console.log('🏠 Dashboard: Component unmounting');
            // Don't disconnect here - let it persist across navigation
        };
    }, [user?._id]);

    // Fetch user profile
    const { data: profileData, refetch: refetchProfile, isLoading: profileLoading } = useQuery({
        queryKey: ['userProfile'],
        queryFn: async () => {
            const response = await apiMethods.users.getProfile();
            return response.data;
        }
    });

    const profile = profileData?.user;
    const { data: unreadConnections = 0 } = useUnreadConnections();

    // Mark this browser as the card owner's so the public card shows owner tools here
    useEffect(() => {
        rememberOwnCard(profile?.cardUuid);
    }, [profile?.cardUuid]);

    // Fetch user stats
    const { data: statsData, isLoading: statsLoading } = useQuery({
        queryKey: ['userStats'],
        queryFn: async () => {
            const response = await apiMethods.users.getStats();
            return response.data;
        }
    });

    const stats = statsData?.stats;

    // Fetch card analytics
    const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
        queryKey: ['cardAnalytics', profile?.cardUuid],
        queryFn: async () => {
            if (!profile?.cardUuid) return null;
            const response = await apiMethods.card.getAnalytics(profile.cardUuid);
            return response.data;
        },
        enabled: !!profile?.cardUuid
    });

    const analytics = analyticsData?.analytics;

    const cardUrl = profile?.cardUrl || (profile?.cardUuid ? `${window.location.origin}/card/${profile.cardUuid}` : '');

    const copyToClipboard = () => {
        navigator.clipboard.writeText(cardUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const downloadQRCode = async () => {
        if (!profile?.cardUuid) return;

        try {
            const response = await apiMethods.card.getQRCode(profile.cardUuid, {
                format: 'png',
                size: 300
            });

            // Create a blob from the response
            const blob = new Blob([response.data], { type: 'image/png' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'business-card-qr.png');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download QR code:', error);
        }
    };

    const StatCard = ({ icon: Icon, label, value, subValue, loading }) => (
        <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">{label}</p>
                    <p className="mt-2 text-3xl font-semibold text-gray-900">
                        {loading ? '-' : value}
                    </p>
                    {subValue && !loading && (
                        <p className="mt-1 text-sm text-gray-500">{subValue}</p>
                    )}
                </div>
                <div className="bg-primary-100 p-3 rounded-full">
                    <Icon className="h-6 w-6 text-primary-600" />
                </div>
            </div>
        </div>
    );

    const DeviceChart = ({ mobile, desktop, tablet }) => {
        const total = mobile + desktop + tablet;
        if (total === 0) return null;

        return (
            <div className="space-y-2">
                <div>
                    <div className="flex justify-between text-sm mb-1">
                        <span>Mobile</span>
                        <span>{mobile}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${mobile}%` }} />
                    </div>
                </div>
                <div>
                    <div className="flex justify-between text-sm mb-1">
                        <span>Desktop</span>
                        <span>{desktop}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full" style={{ width: `${desktop}%` }} />
                    </div>
                </div>
                <div>
                    <div className="flex justify-between text-sm mb-1">
                        <span>Tablet</span>
                        <span>{tablet}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${tablet}%` }} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <AnnouncementWidget />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Welcome Section */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">
                        Welcome back, {profile?.profile?.name || user?.profile?.name || user?.email || 'User'}!
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Here's an overview of your digital business card.
                    </p>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-lg shadow mb-8">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <Link
                                to="/user/profile"
                                className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Profile
                            </Link>

                            <Link
                                to="/user/card-preview"
                                className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                <Eye className="h-4 w-4 mr-2" />
                                Preview Card
                            </Link>

                            <button
                                onClick={downloadQRCode}
                                disabled={!profile?.cardUuid}
                                className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <QrCode className="h-4 w-4 mr-2" />
                                Download QR
                            </button>

                            <a
                                href={cardUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${
                                    !profile?.cardUuid ? 'pointer-events-none opacity-50' : ''
                                }`}
                            >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                View Public Card
                            </a>
                        </div>
                    </div>
                </div>

                {/* Card URL */}
                {profile?.cardUuid && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                        <h3 className="text-sm font-medium text-blue-900 mb-2">Your Business Card URL</h3>
                        <div className="flex items-center space-x-2">
                            <input
                                type="text"
                                readOnly
                                value={cardUrl}
                                className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded-md text-sm"
                            />
                            <button
                                onClick={copyToClipboard}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                            >
                                <Copy className="h-4 w-4 mr-2" />
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    </div>
                )}

                {/* New connections */}
                {unreadConnections > 0 && (
                    <Link
                        to="/user/connections"
                        className="group mb-8 flex items-center gap-4 rounded-lg bg-white p-5 shadow ring-1 ring-primary-100 hover:ring-primary-300 transition"
                    >
                        <div className="relative bg-primary-100 p-3 rounded-full">
                            <Contact className="h-6 w-6 text-primary-600" />
                            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-600">New connections</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {unreadConnections} {unreadConnections === 1 ? 'person' : 'people'} shared their contact with you
                            </p>
                        </div>
                        <span className="hidden sm:inline text-sm font-medium text-primary-600 group-hover:text-primary-700">View</span>
                        <ChevronRight className="h-5 w-5 text-primary-600" />
                    </Link>
                )}

                {/* Analytics Section */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-gray-900">Card Performance</h2>
                        <button
                            onClick={() => refetchProfile()}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Refresh
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <StatCard
                            icon={Eye}
                            label="Total Views"
                            value={analytics?.totalViews || 0}
                            subValue={`${analytics?.viewsThisMonth || 0} this month`}
                            loading={analyticsLoading}
                        />
                        <StatCard
                            icon={Users}
                            label="Unique Visitors"
                            value={analytics?.uniqueVisitors || 0}
                            loading={analyticsLoading}
                        />
                        <StatCard
                            icon={User}
                            label="Profile Completion"
                            value={`${stats?.profileCompleteness || 0}%`}
                            loading={statsLoading}
                        />
                        <StatCard
                            icon={Link2}
                            label="Social Links"
                            value={stats?.socialMediaLinks || 0}
                            loading={statsLoading}
                        />
                    </div>
                </div>

                {/* Additional Stats and Live Features */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                    {/* Live Notifications */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Activity</h3>
                        <LiveNotifications userId={user?._id} showToasts={true} maxNotifications={5} />
                    </div>

                    {/* Device Types */}
                    {analytics?.deviceTypes && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Types</h3>
                            <DeviceChart {...analytics.deviceTypes} />
                        </div>
                    )}

                    {/* Security Features */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Security</h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Two-Factor Authentication</span>
                                <span className={`text-sm font-medium ${
                                    stats?.securityFeatures?.twoFactorEnabled ? 'text-green-600' : 'text-gray-500'
                                }`}>
                                    {stats?.securityFeatures?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Passkeys</span>
                                <span className="text-sm font-medium text-gray-900">
                                    {stats?.securityFeatures?.passkeysCount || 0} configured
                                </span>
                            </div>
                            {!stats?.securityFeatures?.twoFactorEnabled && (
                                <Link
                                    to="/user/security"
                                    className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mt-2"
                                >
                                    <Shield className="h-4 w-4 mr-1" />
                                    Enhance Security
                                </Link>
                            )}
                        </div>
                    </div>
                </div>

                {/* Profile Completion */}
                {stats?.profileCompleteness < 100 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <h3 className="text-sm font-medium text-yellow-900 mb-2">
                            Complete Your Profile
                        </h3>
                        <p className="text-sm text-yellow-700 mb-4">
                            Your profile is {stats?.profileCompleteness || 0}% complete. Add more information to make your business card more effective.
                        </p>
                        <div className="w-full bg-yellow-200 rounded-full h-2 mb-4">
                            <div
                                className="bg-yellow-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${stats?.profileCompleteness || 0}%` }}
                            />
                        </div>
                        <Link
                            to="/user/profile"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-yellow-900 bg-yellow-100 hover:bg-yellow-200"
                        >
                            Complete Profile
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;